import { useEffect, useRef, useState } from 'react';
import { PasswiserLoginsSDK, LoginFlow, AuthError, type AuthResult } from '@passwiser/sdk';
import { authConfig } from './config';
import './App.css';

const STORAGE_KEYS = {
  accessToken: 'siwe_access_token',
  idToken: 'siwe_id_token',
  tokenType: 'siwe_token_type',
  scope: 'siwe_scope',
  expiresIn: 'siwe_expires_in',
} as const;

function getStoredTokens() {
  const idToken = localStorage.getItem(STORAGE_KEYS.idToken);
  const accessToken = localStorage.getItem(STORAGE_KEYS.accessToken);
  if (!idToken || !accessToken) return null;
  const expiresIn = localStorage.getItem(STORAGE_KEYS.expiresIn);
  return {
    access_token: accessToken,
    id_token: idToken,
    token_type: localStorage.getItem(STORAGE_KEYS.tokenType) ?? 'Bearer',
    scope: localStorage.getItem(STORAGE_KEYS.scope) ?? '',
    expires_in: expiresIn ? Number(expiresIn) : 0,
  };
}

function saveTokens(tokens: AuthResult) {
  if (tokens.access_token) localStorage.setItem(STORAGE_KEYS.accessToken, String(tokens.access_token));
  if (tokens.id_token) localStorage.setItem(STORAGE_KEYS.idToken, String(tokens.id_token));
  if (tokens.token_type) localStorage.setItem(STORAGE_KEYS.tokenType, String(tokens.token_type));
  if (tokens.scope) localStorage.setItem(STORAGE_KEYS.scope, String(tokens.scope));
  if (tokens.expires_in != null) localStorage.setItem(STORAGE_KEYS.expiresIn, String(tokens.expires_in));
}

function clearTokens() {
  Object.values(STORAGE_KEYS).forEach((k) => localStorage.removeItem(k));
}

const sdk = new PasswiserLoginsSDK({
  clientId: authConfig.clientId,
  baseUrl: authConfig.baseUrl,
  scope: authConfig.scope,
  walletConnectProjectId: authConfig.walletConnectProjectId,
  walletConnectChains: authConfig.walletConnectChains,
  redirectUri: authConfig.redirectUri,
});

export default function App() {
  const [tokens, setTokens] = useState<AuthResult | null>(() => getStoredTokens());
  const [loading, setLoading] = useState(false);
  const [loadingFlow, setLoadingFlow] = useState<LoginFlow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<'access' | 'id' | null>(null);
  const copyResetTimerRef = useRef<number | null>(null);
  const walletConnectReady = Boolean(authConfig.walletConnectProjectId);

  useEffect(() => {
    const run = async () => {
      try {
        const result = await sdk.handleOAuthCallback();
        if (result) {
          saveTokens(result);
          setTokens(result);
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } catch {
        // ignore
      }
    };
    run();
  }, []);

  useEffect(() => {
    return () => {
      if (copyResetTimerRef.current != null) window.clearTimeout(copyResetTimerRef.current);
    };
  }, []);

  const copyToClipboard = async (key: 'access' | 'id', text: string) => {
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement('textarea');
        el.value = text;
        el.setAttribute('readonly', 'true');
        el.style.position = 'fixed';
        el.style.left = '-9999px';
        el.style.top = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }

      setCopiedKey(key);
      if (copyResetTimerRef.current != null) window.clearTimeout(copyResetTimerRef.current);
      copyResetTimerRef.current = window.setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      // ignore
    }
  };

  const handleWalletConnectLogin = async () => {
    setError(null);
    setLoading(true);
    setLoadingFlow(LoginFlow.SIWE_WALLETCONNECT);
    try {
      const result = await sdk.login(LoginFlow.SIWE_WALLETCONNECT);
      saveTokens(result);
      setTokens(result);
    } catch (err: unknown) {
      const message = err instanceof AuthError ? err.message : err instanceof Error ? err.message : 'Login failed';
      setError(message);
    } finally {
      setLoading(false);
      setLoadingFlow(null);
    }
  };

  const handleReturn = () => {
    clearTokens();
    setTokens(null);
    setError(null);
  };

  return (
    <div className="app">
      <header className="header">
        <div className="kicker">
          <span className="kicker-brand">Passwiser</span>
          <span className="kicker-pill">Mobile</span>
        </div>
        <h1 className="title">SIWE Mobile</h1>
        <p className="subtitle">Sign-in with Ethereum · WalletConnect</p>
      </header>

      <main className="main">
        {!tokens ? (
          <>
            <p className="intro">A mobile-first SIWE flow. Connect your wallet and approve a sign-in message.</p>
            {error && <div className="error" role="alert">{error}</div>}
            <div className="actions">
              <button
                type="button"
                className="btn btn-walletconnect"
                onClick={handleWalletConnectLogin}
                disabled={loading || !walletConnectReady}
                aria-busy={loadingFlow === LoginFlow.SIWE_WALLETCONNECT}
              >
                {loadingFlow === LoginFlow.SIWE_WALLETCONNECT
                  ? 'Connecting…'
                  : 'Connect wallet (WalletConnect)'}
              </button>
            </div>
            {!walletConnectReady ? (
              <p className="hint" role="note">
                Missing WalletConnect project id. Set <code>VITE_WALLETCONNECT_PROJECT_ID</code> and restart the dev
                server.
              </p>
            ) : (
              <p className="hint">You’ll be prompted in your wallet app to connect and sign a message.</p>
            )}
          </>
        ) : (
          <div className="tokens-panel">
            <h2 className="tokens-title">Signed in</h2>
            <div className="token-row">
              <span className="token-label">Access token</span>
              <div className="token-controls">
                <code className="token-value token-value-scroll">{tokens.access_token ?? ''}</code>
                <button
                  type="button"
                  className="token-copy-btn"
                  onClick={() => copyToClipboard('access', tokens.access_token ?? '')}
                  disabled={!tokens.access_token}
                >
                  {copiedKey === 'access' ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
            <div className="token-row">
              <span className="token-label">ID token</span>
              <div className="token-controls">
                <code className="token-value token-value-scroll">{tokens.id_token ?? ''}</code>
                <button
                  type="button"
                  className="token-copy-btn"
                  onClick={() => copyToClipboard('id', tokens.id_token ?? '')}
                  disabled={!tokens.id_token}
                >
                  {copiedKey === 'id' ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
            <div className="token-row">
              <span className="token-label">Expires in</span>
              <code className="token-value">{tokens.expires_in} s</code>
            </div>
            <button type="button" className="btn btn-return" onClick={handleReturn}>
              Return
            </button>
          </div>
        )}
      </main>

      <footer className="footer">
        <span>Passwiser SIWE Mobile Sample · React</span>
      </footer>
    </div>
  );
}
